/**
 * Packages this app's in-memory pipeline outputs into a .zip using the real
 * reference pipeline's own on-disk directory structure and filenames
 * (output/sub-{id}/{DWI,PWI}/{preprocess,registration,segment|segmentation,
 * reporting}/sub-{id}_{channel}_space-{space}_{stage}.nii.gz), so this app's
 * WASM output is directly diffable against real reference outputs by anyone
 * -- not just during a live debugging session with the real reference tree
 * mounted.
 *
 * Real directory-structure source (read directly, not guessed):
 *   /media/joshua/PortableLab/keep/wt_final/output/sub-02e8eb42/PWI/
 *   /media/joshua/PortableLab/keep/wt_final/output/sub-05a971ae/DWI/
 * Note the real naming is NOT uniform across modalities: DWI's lesion-output
 * folder is "segment", PWI's is "segmentation" -- preserved here exactly,
 * not normalized to one spelling.
 *
 * Only ships files this app actually computes and retains in StageState; a
 * real file this app has no corresponding in-memory data for (e.g. raw B0,
 * ANTs' full SyN-space PWI channels, the ASPECTS CSVs) is simply omitted,
 * never fabricated. manifest.txt in the zip root lists exactly what was
 * included vs. intentionally omitted for this run, plus the handful of
 * known real-format deviations (listed in FORMAT_NOTES below).
 */
import { writeNiftiFloat32, type NiftiVolume } from "./nifti";
import type { Dims } from "./volume";

const FORMAT_NOTES = [
  "- Masks are written as float32 NIfTI (0.0/1.0) rather than the real pipeline's uint8 -- numerically identical, different on-disk dtype. Every tool used to diff against the real reference (fsleyes, ITK-SNAP, nibabel) reads either fine.",
  "- *.nii.gz here is produced by this app's own minimal NIfTI-1 writer + the browser's native CompressionStream('gzip'), not nibabel/zlib -- valid gzip and valid NIfTI-1, but not necessarily byte-identical compressed output to the real files even when the voxel data matches exactly.",
  "- ANTs transform files (*.mat) ARE the real antsRegistration WASM binary's own output bytes, shipped verbatim -- not re-derived or approximated.",
  "- The automatic radiological report is shipped as .html (this app's actual output format) rather than the real pipeline's .txt, since the content itself is HTML, not plain text.",
  "- metrics.json here only contains what this app actually computes (Dice vs. its own bundled ground truth, lesion voxel count) -- not the full real metrics.json schema (orig_postproc space, per-structure breakdowns, etc).",
].join("\n");

export interface ExportFile {
  path: string; // zip-relative path, forward slashes, no leading slash
  bytes: Uint8Array;
}

async function gzip(bytes: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream("gzip");
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(cs);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Also used by `tools/segment/InteractiveSegPanel.tsx` to save a SAM mask. */
export async function niftiGz(vol: NiftiVolume): Promise<Uint8Array> {
  return gzip(writeNiftiFloat32(vol));
}

/** A binary mask as a displayable/saveable float volume — shared with any
 *  caller that needs to turn a raw 0/1 mask into a real `.nii.gz`, not just
 *  the export manifest below (see `tools/stageRun/StageRunPanel.tsx`). */
export function maskToVol(mask: Uint8Array, dims: Dims, pixdim: Dims, affine: Float32Array): NiftiVolume {
  const voxels = new Float32Array(mask.length);
  for (let i = 0; i < mask.length; i++) voxels[i] = mask[i];
  return { dims, pixdim, affine, voxels, datatype: 16 };
}

// --- minimal ZIP writer (store method only -- .nii.gz payloads are already
// gzip-compressed, and the handful of text/json entries are tiny, so a
// second compression pass buys nothing). No external dependency: this
// project has no zip library in package.json and the format is small enough
// to hand-write correctly (local header + central directory + EOCD, CRC32). ---

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

export function buildZip(files: ExportFile[]): Uint8Array {
  const localChunks: Uint8Array[] = [];
  const centralChunks: Uint8Array[] = [];
  let offset = 0;

  for (const f of files) {
    const nameBytes = utf8(f.path);
    const crc = crc32(f.bytes);
    const size = f.bytes.length;

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0, true); // flags
    lv.setUint16(8, 0, true); // method: store
    lv.setUint16(10, 0, true); // mod time
    lv.setUint16(12, 0x21, true); // mod date (1980-01-01, placeholder)
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true); // compressed size
    lv.setUint32(22, size, true); // uncompressed size
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true); // extra length
    local.set(nameBytes, 30);
    localChunks.push(local, f.bytes);

    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0, true); // flags
    cv.setUint16(10, 0, true); // method
    cv.setUint16(12, 0, true); // mod time
    cv.setUint16(14, 0x21, true); // mod date
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true); // extra length
    cv.setUint16(32, 0, true); // comment length
    cv.setUint16(34, 0, true); // disk number start
    cv.setUint16(36, 0, true); // internal attrs
    cv.setUint32(38, 0, true); // external attrs
    cv.setUint32(42, offset, true); // local header offset
    central.set(nameBytes, 46);
    centralChunks.push(central);

    offset += local.length + f.bytes.length;
  }

  const centralStart = offset;
  let centralSize = 0;
  for (const c of centralChunks) centralSize += c.length;

  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(4, 0, true);
  ev.setUint16(6, 0, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, centralStart, true);
  ev.setUint16(20, 0, true);

  const totalSize = offset + centralSize + eocd.length;
  const out = new Uint8Array(totalSize);
  let pos = 0;
  for (const c of localChunks) { out.set(c, pos); pos += c.length; }
  for (const c of centralChunks) { out.set(c, pos); pos += c.length; }
  out.set(eocd, pos);
  return out;
}

// --- manifest: StageState -> real-filename ExportFile list ---

// Mirrors main.ts's StageState shape without importing it (export.ts has no
// dependency on main.ts, keeping the worker-bundling / module graph simple).
export interface ExportableState {
  modality: "DWI" | "PWI";
  subjectId: string;
  prepData: NiftiVolume | null;
  prepDataName: string | null;
  brainMask: Uint8Array | null;
  affineDwi: NiftiVolume | null;
  affineAdc: NiftiVolume | null;
  affineMask: NiftiVolume | null;
  dwiAffineTransform: ArrayBuffer | null;
  registeredDwi: NiftiVolume | null;
  registeredAdc: NiftiVolume | null;
  registeredMask: Uint8Array | null;
  pwiMask: Uint8Array | null;
  pwiMaskRef: NiftiVolume | null;
  pwiTtpAff: NiftiVolume | null;
  pwiDwiDescNorm: NiftiVolume | null;
  pwiAdcDescNorm: NiftiVolume | null;
  pwiTtpDescNorm: NiftiVolume | null;
  pwiSymClassic: NiftiVolume | null;
  lesionMask: Uint8Array | null;
  lesionDims: Dims | null;
  lesionPixdim: Dims | null;
  lesionAffine: Float32Array | null;
  lesionDice: number | null;
  reportHtml: string | null;
}

export async function buildExportManifest(s: ExportableState): Promise<ExportFile[]> {
  const id = s.subjectId;
  const files: ExportFile[] = [];
  const included: string[] = [];
  const push = async (path: string, vol: NiftiVolume | null) => {
    if (!vol) return;
    files.push({ path, bytes: await niftiGz(vol) });
    included.push(path);
  };

  if (s.modality === "DWI") {
    const root = `output/${id}/DWI`;

    if (s.prepData) {
      const isB0 = /b0/i.test(s.prepDataName ?? "");
      await push(`${root}/preprocess/${id}_${isB0 ? "B0" : "DWI"}.nii.gz`, s.prepData);
      if (s.brainMask) {
        await push(`${root}/preprocess/${id}_DWIbrain-mask.nii.gz`, maskToVol(s.brainMask, s.prepData.dims, s.prepData.pixdim, s.prepData.affine));
      }
    }

    await push(`${root}/registration/${id}_DWI_space-MNI152_aff.nii.gz`, s.affineDwi);
    await push(`${root}/registration/${id}_ADC_space-MNI152_aff.nii.gz`, s.affineAdc);
    await push(`${root}/registration/${id}_DWIbrain-mask_space-MNI152_aff.nii.gz`, s.affineMask);
    await push(`${root}/registration/${id}_DWI_space-MNI152_affsyn.nii.gz`, s.registeredDwi);
    await push(`${root}/registration/${id}_ADC_space-MNI152_affsyn.nii.gz`, s.registeredAdc);
    if (s.registeredMask && s.registeredAdc) {
      await push(
        `${root}/registration/${id}_DWIbrain-mask_space-MNI152_affsyn.nii.gz`,
        maskToVol(s.registeredMask, s.registeredAdc.dims, s.registeredAdc.pixdim, s.registeredAdc.affine)
      );
    }
    if (s.dwiAffineTransform) {
      files.push({ path: `${root}/registration/${id}_aff_space-individual2MNI152.mat`, bytes: new Uint8Array(s.dwiAffineTransform) });
      included.push(`${root}/registration/${id}_aff_space-individual2MNI152.mat`);
    }

    if (s.lesionMask && s.lesionDims && s.lesionPixdim && s.lesionAffine) {
      // Always affine-space, matching the real pipeline's primary prediction
      // filename (segmentation_paths.py: no "_affsyn" suffix on pred_mni) --
      // DAGMNet DWI inference runs on _aff, not _affsyn, confirmed against
      // scripts/run_ads_dwi.py. registeredDwi's presence doesn't change this.
      await push(`${root}/segment/${id}_stroke-mask_space-MNI152.nii.gz`, maskToVol(s.lesionMask, s.lesionDims, s.lesionPixdim, s.lesionAffine));
    }

    if (s.reportHtml) {
      files.push({ path: `${root}/reporting/${id}_automatic_radiological_report.html`, bytes: utf8(s.reportHtml) });
      included.push(`${root}/reporting/${id}_automatic_radiological_report.html`);
    }
    if (s.lesionMask && s.lesionDice !== null) {
      const metrics = { subject_id: id, modality: "DWI", lesion_voxels: s.lesionMask.reduce((a, v) => a + (v ? 1 : 0), 0), dice_vs_bundled_ground_truth: s.lesionDice };
      files.push({ path: `${root}/reporting/${id}_metrics.json`, bytes: utf8(JSON.stringify(metrics, null, 2)) });
      included.push(`${root}/reporting/${id}_metrics.json`);
    }
  } else {
    const root = `output/${id}/PWI`;

    if (s.pwiMask && s.pwiMaskRef) {
      await push(`${root}/preprocess/${id}_PWIbrain-mask.nii.gz`, maskToVol(s.pwiMask, s.pwiMaskRef.dims, s.pwiMaskRef.pixdim, s.pwiMaskRef.affine));
    }
    if (s.brainMask && s.prepData) {
      await push(`${root}/preprocess/${id}_DWIbrain-mask.nii.gz`, maskToVol(s.brainMask, s.prepData.dims, s.prepData.pixdim, s.prepData.affine));
    }

    await push(`${root}/registration/${id}_DWI_space-MNI152_aff.nii.gz`, s.affineDwi);
    await push(`${root}/registration/${id}_ADC_space-MNI152_aff.nii.gz`, s.affineAdc);
    await push(`${root}/registration/${id}_TTP_space-MNI152_aff.nii.gz`, s.pwiTtpAff);
    await push(`${root}/registration/${id}_DWIbrain-mask_space-MNI152_aff.nii.gz`, s.affineMask);
    await push(`${root}/registration/${id}_DWI_space-MNI152_aff_desc-norm.nii.gz`, s.pwiDwiDescNorm);
    await push(`${root}/registration/${id}_ADC_space-MNI152_aff_desc-norm.nii.gz`, s.pwiAdcDescNorm);
    await push(`${root}/registration/${id}_TTP_space-MNI152_aff_desc-norm.nii.gz`, s.pwiTtpDescNorm);

    await push(`${root}/segmentation/${id}_HPsymclassic-mask_space-MNI152.nii.gz`, s.pwiSymClassic);
    if (s.lesionMask && s.lesionDims && s.lesionPixdim && s.lesionAffine) {
      await push(`${root}/segmentation/${id}_HP-mask_space-MNI152.nii.gz`, maskToVol(s.lesionMask, s.lesionDims, s.lesionPixdim, s.lesionAffine));
    }

    if (s.reportHtml) {
      files.push({ path: `${root}/reporting/${id}_automatic_radiological_report.html`, bytes: utf8(s.reportHtml) });
      included.push(`${root}/reporting/${id}_automatic_radiological_report.html`);
    }
    if (s.lesionMask && s.lesionDice !== null) {
      const metrics = { subject_id: id, modality: "PWI", lesion_voxels: s.lesionMask.reduce((a, v) => a + (v ? 1 : 0), 0), dice_vs_bundled_ground_truth: s.lesionDice };
      files.push({ path: `${root}/segmentation/${id}_metrics.json`, bytes: utf8(JSON.stringify(metrics, null, 2)) });
      included.push(`${root}/segmentation/${id}_metrics.json`);
    }
  }

  const manifestTxt =
    `openads-wasm export -- ${s.modality} pipeline run for ${id}\n` +
    `Generated ${new Date().toISOString()}\n\n` +
    `Included (${included.length} files):\n` +
    included.map((p) => `  ${p}`).join("\n") +
    `\n\nFormat notes / known deviations from the real reference pipeline's own output:\n${FORMAT_NOTES}\n\n` +
    `Any real reference file not listed above was not included because this app does not retain that ` +
    `intermediate in memory for this run (e.g. it was never computed this session, or is discarded after use) ` +
    `-- never because it was fabricated and then omitted.\n`;
  files.unshift({ path: `output/${id}/${s.modality}/manifest.txt`, bytes: utf8(manifestTxt) });

  return files;
}
